/**
 * Proposals service — Fee Proposals (`tblZvesr5gZ7HtDWz`). The fee math lives in Airtable formulas;
 * this layer writes INPUTS and reads computed OUTPUTS (FPV, %, per-phase fees, package totals). No
 * fields[] on reads (the table is dense with lookups/formulas that vanish under fields[]).
 *
 * Step 6a covers the data layer + read/display (list, detail, computed fee breakdown). Authoring of
 * the full input set, the native PDF, and in-journey sign follow in 6b / 7 / 8.
 */
import { apiGet, apiGetOne, apiPatch, apiPost } from './_api.js'

const TBL_PROP = 'tblZvesr5gZ7HtDWz'

export const PROPOSAL_TYPES = ['Basic Services', 'Design Consultation', 'ASR Proposal']

// ASR Proposal has no HTML web-view body (api/proposal-pdf.js's previewProposalHtml path returns
// no_web_view for it) — only Basic Services and Design Consultation render one.
export const HAS_WEB_VIEW = new Set(['Basic Services', 'Design Consultation'])

// Client-facing display name for a proposal type — distinct from the internal Airtable type
// string. 'Basic Services'/'Design Consultation' alone read as ambiguous fragments outside the CRM
// (e.g. the Client Hub's "new update" email just said `"Design Consultation" has been added`,
// with no indication it was a proposal at all) — this is what shows in that copy instead. Matt also
// asked 'Basic Services' specifically to read as 'Architectural Services' to the client, not just
// gain a suffix. 'ASR Proposal' already reads fine on its own.
export const PROPOSAL_DISPLAY_TITLE = {
  'Basic Services': 'Architectural Services Proposal',
  'Design Consultation': 'Design Consultation Proposal',
  'ASR Proposal': 'ASR Proposal',
}
export function proposalDisplayTitle(type) {
  return PROPOSAL_DISPLAY_TITLE[type] || (type ? `${type} Proposal` : 'Proposal')
}

// Phase fee breakdown (read-only computed columns).
//
// This is also the PHASE CATALOG the proposal DOCUMENT renders from — it mirrors `PHASE_SPEC` in
// `api/_proposalHtml.js` (code, the `Phases Included` option string, suite tag, and the
// always-hourly Permitting rule), so an internal view of a proposal's phases can show exactly what
// the client's copy shows. `phase` is the multi-select option that decides inclusion; `adjustKey`
// is the Airtable percent field that fine-tunes that phase's fee (only four phases have one);
// `suiteTag` marks the phases a tiered proposal only sells above a certain suite.
//
// KEEP IN SYNC with api/_proposalHtml.js#PHASE_SPEC. That file is server-side and can't be
// imported from the browser bundle, so this is a deliberate mirror of its identifying fields —
// not of its prose, which stays there. See docs/CRM.md ("Phase breakdown mirrors the document").
export const PROPOSAL_PHASES = [
  { code: 'PD', label: 'Pre-design & Planning',   phase: 'Pre-design & Planning Phase' },
  { code: 'SD', label: 'Schematic Design',        phase: 'Schematic Design Phase',        adjustKey: 'SD Adjustment' },
  { code: 'DA', label: 'Discretionary Approvals', phase: 'Discretionary Approvals Phase', adjustKey: 'DA Adjustment' },
  { code: 'DD', label: 'Design Development',      phase: 'Design Development Phase' },
  { code: 'CD', label: 'Construction Documents',  phase: 'Construction Documents Phase' },
  // Permitting is ALWAYS billed hourly — `_proposalHtml.js#feeMatrix` skips it outright
  // (`if (spec.code === 'PR') continue`) and Exhibit C prints "Hourly" for it — so showing its
  // stored `PR Fee` as a fixed fee anywhere internally contradicts the document.
  { code: 'PR', label: 'Permitting',              phase: 'Permitting Phase', hourly: true },
  { code: 'AD', label: 'Architectural Detailing', phase: 'Architectural Detailing Phase', adjustKey: 'AD Adjustment', suiteTag: 'Premier Architecture Suite' },
  { code: 'IA', label: 'Interior Architecture',   phase: 'Interior Architecture Phase',   adjustKey: 'IA Adjustment', suiteTag: 'Architecture + Interiors Suite' },
]

/** Appended to every Basic Services proposal's Exhibit C, always hourly, never selectable. */
export const PROPOSAL_TRAILING_PHASES = [
  { code: 'BN', label: 'Bidding & Negotiation', hourly: true },
  { code: 'CS', label: 'Construction Support',  hourly: true },
]

function num(v) { const n = Number(v); return isNaN(n) ? 0 : n }
function getFirst(v) { return Array.isArray(v) ? v[0] : v }
function attUrl(v) { return Array.isArray(v) && v[0] ? v[0].url : null }

function normProposal(r) {
  const f = (r && r.fields) || {}
  const phases = PROPOSAL_PHASES.map(p => ({
    code: p.code, label: p.label,
    fee: num(f[`${p.code} Fee`]),
    hours: num(f[`${p.code} Hours`]),
    pct: f[`${p.code} Fee Percent`] != null ? num(f[`${p.code} Fee Percent`]) : null,
  }))
  const displayStatus = f['Display Status'] || f['Status'] || (f['Posted'] ? 'Posted' : 'Draft')
  return {
    id: r.id,
    name: f['Name'] || '',
    type: f['Fee Proposal Type'] || '',
    status: displayStatus,
    posted: !!f['Posted'],
    accepted: !!f['Accepted'],
    declined: !!f['Declined'],
    // NOT checkboxes despite reading like them. 'Tiered Pricing' is a Yes/No singleSelect and
    // 'Expired' a formula returning the STRINGS 'True'/'False' -- so `!!` was true for every
    // record with any value at all (measured: 6 of 40 recent proposals say 'No' to tiered and were
    // all treated as tiered; every single one read as expired). Match the document, which tests
    // `=== 'Yes'` (api/proposal-pdf.js). 'Posted'/'Accepted'/'Declined' above ARE real checkboxes.
    expired: f['Expired'] === 'True' || f['Expired'] === true,
    opportunityId: getFirst(f['Opportunity']) || null,
    projectName: getFirst(f['Project Name (from Opportunity)']) || getFirst(f['Project Name (from Projects)']) || '',
    clientName: f['Client Name on Proposal'] || getFirst(f['Client (from Opportunity)']) || '',
    feeModelId: getFirst(f['Fee Model']) || null,
    typeOfProject: f['Type of Project'] || '',
    proposalDate: f['Proposal Date'] || null,
    expirationDate: f['Expiration Date'] || null,
    expirationTimeline: f['Expiration Timeline'] || null,
    // computed fee outputs
    fpv: num(f['Factored Project Value']),
    baseFeePct: f['Base Fee Percentage'] != null ? num(f['Base Fee Percentage']) : null,
    weightedFeePct: f['Weighted Fee Percentage'] != null ? num(f['Weighted Fee Percentage']) : null,
    weightedFeeAmount: num(f['Weighted Fee Amount']),
    contractFee: num(f['Contract Fee']),
    totalHours: num(f['Total Budget Hours']),
    retainer: num(f['Retainer Amount']),
    estimatedBsFpv: num(f['Estimated BS FPV']),
    packages: {
      A: num(f['Package A']), B: num(f['Package B']), C: num(f['Package C']),
    },
    tiered: f['Tiered Pricing'] === 'Yes' || f['Tiered Pricing'] === true,
    selectedPackage: f['Selected Package'] || '',
    phases,
    postedPdf: attUrl(f['Posted Proposal PDF']),
    signedAgreement: attUrl(f['Signed Agreement']),
    raw: f,
  }
}

/* ── Phase breakdown — what the DOCUMENT actually shows ──────────────────── */

/**
 * Which fee presentation the document uses. Mirrors `api/proposal-pdf.js`:
 *   STANDARD  — the plain fee
 *   FRIENDS   — standard struck through, Industry Partner fee beside it
 *   HOURLY    — no fixed fee at all; every phase reads "Hourly"
 * `Fee to Use` is an Airtable formula and normally decides this on its own; the two fallbacks
 * cover a record where it hasn't resolved.
 */
export function proposalFeeMode(raw) {
  const f = raw || {}
  const yes = v => v === true || v === 'Yes'
  return f['Fee to Use'] || (yes(f['All Hourly Fees']) ? 'HOURLY' : (yes(f['Friends and Family Discount']) ? 'FRIENDS' : 'STANDARD'))
}

/**
 * The proposal's phases exactly as its own document presents them, for internal views that must
 * not disagree with the client's copy.
 *
 * The rules are the document's, not this file's invention (`api/_proposalHtml.js`):
 *   - INCLUSION is the `Phases Included` multi-select, never "does this phase have a fee > 0".
 *     Those are different questions, and using the fee as a proxy got both directions wrong: an
 *     included phase priced at 0 vanished, and an excluded phase carrying a stale fee showed up.
 *   - PERMITTING is always hourly (`feeMatrix` skips it entirely), regardless of its stored fee.
 *   - BIDDING & NEGOTIATION and CONSTRUCTION SUPPORT are appended to every proposal, always hourly.
 *   - The FRIENDS / HOURLY presentations replace the fee figure, they don't annotate it.
 *
 * Excluded phases come back as rows too (`included: false`, no fee) — they are NOT on the client's
 * document, and a caller rendering this must show them as visibly excluded rather than as ordinary
 * rows. They're returned so an internal reader can tell "deliberately out of scope" apart from
 * "missing data", which a filtered list can't express.
 *
 * Returns { feeMode, tiered, rows, total, totalNoFF, totalLabel }.
 */
export function proposalPhaseBreakdown(p, override) {
  const f = (p && p.raw) || {}
  // An all-inclusive fixed fee replaces the engine's numbers on the document, so it has to replace
  // them here too — this table exists precisely so an internal reader never sees a different price
  // from the client's copy. `covered` is the phases the manual fee actually bought.
  const ai = override && override.phaseFees ? override : null
  const covered = code => !!(ai && Object.prototype.hasOwnProperty.call(ai.phaseFees, code))
  const feeMode = ai ? 'STANDARD' : proposalFeeMode(f)
  const included = Array.isArray(f['Phases Included']) ? f['Phases Included'] : []
  const n = v => { const x = Number(v); return isNaN(x) ? 0 : x }

  const row = (spec, isIncluded) => (covered(spec.code) ? {
    code: spec.code,
    label: spec.label,
    included: true,
    // The whole point of all-inclusive: the phases that are normally hourly now carry a fee.
    hourly: false,
    fee: n(ai.phaseFees[spec.code]),
    feeNoFF: n(ai.phaseFees[spec.code]),
    hours: n(f[`${spec.code} Hours`]),
    adjustment: null,
    suiteTag: spec.suiteTag || null,
    allInclusive: true,
  } : {
    code: spec.code,
    label: spec.label,
    included: isIncluded,
    // Hourly either because this phase always is, or because the whole proposal is.
    hourly: !!spec.hourly || feeMode === 'HOURLY',
    fee: n(f[`${spec.code} Fee`]),
    feeNoFF: n(f[`${spec.code} Fee No FF Discount`]),
    hours: n(f[`${spec.code} Hours`]),
    // The stored fraction (0.45), not a percentage — formatting is the caller's business.
    adjustment: spec.adjustKey && f[spec.adjustKey] != null ? n(f[spec.adjustKey]) : null,
    suiteTag: spec.suiteTag || null,
  })

  const rows = [
    ...PROPOSAL_PHASES.map(spec => row(spec, included.includes(spec.phase))),
    ...PROPOSAL_TRAILING_PHASES.map(spec => row(spec, true)),
  ]

  // 'Contract Fee' is empty on plenty of real records (all six non-tiered proposals in a recent
  // 40-record sample), so a fallback is needed. The document's own fallback
  // (api/proposal-pdf.js: `Number(f['Contract Fee']) || totalFees`) sums ALL eight phase fees --
  // including Permitting, which its own fee matrix deliberately excludes as hourly, and including
  // phases that aren't in 'Phases Included' at all. On a real executed proposal (Osborne Pismo
  // Beach ADU) that prints "Total Fixed Fees $3,930" above rows totalling $2,060, overstated by
  // the exact PR Fee. Deliberately NOT mirrored: this total sums the rows actually shown, so it
  // can never disagree with them. See docs/CRM.md -- the document-side bug is recorded there and
  // is a separate call to make, since it changes a client-facing figure.
  const shownFeeSum = rows.reduce((a, r) => a + (r.included && !r.hourly ? r.fee : 0), 0)
  const shownFeeSumNoFF = rows.reduce((a, r) => a + (r.included && !r.hourly ? r.feeNoFF : 0), 0)

  return {
    feeMode,
    // An all-inclusive fee is a single price — the document collapses to one fee column, so the
    // three-package presentation must not show here either.
    tiered: ai ? false : !!p.tiered,
    allInclusive: !!ai,
    rows,
    total: ai ? n(ai.totalFee) : (n(f['Contract Fee']) || shownFeeSum),
    totalNoFF: ai ? 0 : (n(f['Total No FF Discount']) || shownFeeSumNoFF),
    // The document's own totals-row label; it prints no total at all in HOURLY mode.
    totalLabel: ai ? 'Total Fixed Fee (all-inclusive)'
      : (feeMode === 'FRIENDS' ? 'Total Discounted Fixed Fees' : 'Total Fixed Fees'),
  }
}

/* ── Reads ───────────────────────────────────────────────────────────────── */

/** Recent proposals across the firm (bounded), newest first. */
export async function getProposals({ limit = 120 } = {}) {
  const qs = 'sort%5B0%5D%5Bfield%5D=Proposal+Date&sort%5B0%5D%5Bdirection%5D=desc&maxRecords=' + limit
  const recs = await apiGet(TBL_PROP, qs)
  return recs.map(normProposal)
}

/** Proposals for one opportunity (Project), newest first. */
export async function getProposalsForOpportunity(projectId) {
  const formula = `FIND("${projectId}",ARRAYJOIN({Project Record UID (from Opportunity)},","))`
  const recs = await apiGet(TBL_PROP, 'filterByFormula=' + encodeURIComponent(formula) +
    '&sort%5B0%5D%5Bfield%5D=Proposal+Date&sort%5B0%5D%5Bdirection%5D=desc')
  return recs.map(normProposal)
}

export async function getProposalDetail(id) {
  const r = await apiGetOne(TBL_PROP, id)
  return r ? normProposal(r) : null
}

/* ── Authoring schema (6b) ───────────────────────────────────────────────── */
// Ratings (Complexity/Portfolio/Client) are NOT here — they're lookups inherited from the Opportunity.
export const TYPE_OF_PROJECT_OPTIONS = ['Standard Residential', 'Luxury Residential', 'Standard Commercial', 'Basic Commercial', 'Luxury Commercial']
export const FEE_TO_USE_OPTIONS = ['STANDARD', 'FRIENDS', 'HOURLY']
export const CONTRACT_TYPE_OPTIONS = ['Fixed Fee', 'Hourly']
export const PHASES_INCLUDED_OPTIONS = ['Pre-design & Planning Phase', 'Schematic Design Phase', 'Discretionary Approvals Phase', 'Design Development Phase', 'Construction Documents Phase', 'Permitting Phase', 'Architectural Detailing Phase', 'Interior Architecture Phase']
export const PROJECT_INVOLVES_OPTIONS = ['New Construction', 'Alterations', 'Site Improvements']
// Matches the live Airtable field's choices exactly (Interior Architecture Scope is a
// multipleSelects field, not free text — see the note on the field definition below).
// Matches the live Airtable field's choices exactly — drives {Client Name on Proposal}.
export const CLIENT_NAME_DISPLAY_OPTIONS = ['Organization Name', 'Main Contact Name']
export const INTERIOR_ARCH_SCOPE_OPTIONS = [
  'Space Planning and Furnishing Layouts', 'Wall; Floor; and Ceiling Finishes',
  'Casework Layouts; Details; Finishes; & Hardware', 'Plumbing Fixtures',
  'Appliances / Domestic Equipment', 'Decorative Lighting Layout and Fixtures', 'Furnishing Selections',
]

// type: text | longtext | num | cur | date | select | multiselect | yesno | bool | feemodel | contact | computed
// 'computed' = Airtable formula field (read-only) — writing to it 422s even with typecast:true, so it
// must never appear in a write payload; displayed from the record's own computed value instead.
export const PROPOSAL_INPUT_GROUPS = [
  { title: 'Basics', fields: [
    { key: 'Fee Proposal Type', label: 'Type', type: 'select', options: PROPOSAL_TYPES },
    { key: 'Fee Model', label: 'Fee model', type: 'feemodel' },
    { key: 'Type of Project', label: 'Project type', type: 'select', options: TYPE_OF_PROJECT_OPTIONS },
    // Airtable formula: Design Consultation → Hourly; Basic Services → Hourly when `All Hourly
    // Fees` is ticked, else Fixed Fee. `note` (by proposal type) says so under the value.
    { key: 'Contract Type', label: 'Contract type', type: 'computed', note: {
      'Basic Services': 'Set by "All hourly fees" in Pricing controls: ticked is Hourly, unticked is Fixed Fee.',
      'Design Consultation': 'Always Hourly for a Design Consultation.',
    } },
    { key: 'Proposal Date', label: 'Proposal date', type: 'date' },
    { key: 'Expiration Timeline', label: 'Expiration (days)', type: 'num' },
    // Drives {Client Name on Proposal} (Airtable formula) — left unset, that formula always resolves
    // to the literal text 'INPUTS MISSING' regardless of whether the contacts below are filled in.
    { key: 'Select Client Name for Proposal', label: 'Client name shown as', type: 'select', options: CLIENT_NAME_DISPLAY_OPTIONS },
    { key: 'Client Contact 1', label: 'Client signer 1', type: 'contact' },
    { key: 'Client Contact 2', label: 'Client signer 2', type: 'contact' },
  ] },
  { title: 'Pricing controls', showFor: ['Basic Services'], fields: [
    // Stored values keep Airtable's names; the team reads the client-facing ones.
    { key: 'Fee to Use', label: 'Fee to use', type: 'computed', display: { STANDARD: 'Standard', FRIENDS: 'Industry Partner', HOURLY: 'Hourly' } },
    { key: 'Tiered Pricing', label: 'Tiered pricing', type: 'yesno' },
    { key: 'Include Essential Design Suite', label: 'Essential suite', type: 'yesno' },
    { key: 'Premier Architecture Suite', label: 'Premier suite', type: 'computed' },
    { key: 'Architectural Interiors Suite', label: 'Architecture + Interiors suite', type: 'computed' },
    { key: 'Friends and Family Discount', label: 'Industry Partner discount', type: 'bool' },
    { key: 'All Hourly Fees', label: 'All hourly fees', type: 'bool' },
    { key: 'Use New Fee Curve', label: 'Use new fee curve', type: 'bool' },
    { key: 'Base Fee Percentage Includes Consultants', label: 'Base % includes consultants', type: 'bool' },
  ] },
  // `excludable`: each field here feeds the FPV, and the editor offers "Leave out of fee" on it —
  // the number still prints in Exhibit A, but the Airtable field is left blank (database/388).
  { title: 'Areas (SF unless noted)', showFor: ['Basic Services'], excludable: true, fields: [
    { key: 'New Occupiable Area', label: 'New occupiable', type: 'num' },
    { key: 'Alter Occupiable Area', label: 'Alter occupiable', type: 'num' },
    { key: 'New Garage Area', label: 'New garage', type: 'num' },
    { key: 'Alter Garage Area', label: 'Alter garage', type: 'num' },
    { key: 'New Storage Utility Area', label: 'New storage/utility', type: 'num' },
    { key: 'Alter Storage Utility Area', label: 'Alter storage/utility', type: 'num' },
    { key: 'New Elevated Deck Area', label: 'New elevated deck', type: 'num' },
    { key: 'Alter Elevated Deck Area', label: 'Alter elevated deck', type: 'num' },
    { key: 'New Covered Patio Area', label: 'New covered patio', type: 'num' },
    { key: 'Alter Covered Patio Area', label: 'Alter covered patio', type: 'num' },
    { key: 'Hardscape Area', label: 'Hardscape', type: 'num' },
    { key: 'Alter Exterior Wall Length', label: 'Alter exterior wall (LF)', type: 'num' },
    { key: 'Alter Roofing Area', label: 'Alter roofing', type: 'num' },
    { key: 'Pool Quantity', label: 'Pools (qty)', type: 'num' },
    { key: 'Spa Quantity', label: 'Spas (qty)', type: 'num' },
    { key: 'Site Retaining Wall Length', label: 'Retaining wall (LF)', type: 'num' },
    { key: 'Site Retaining Wall Average Height', label: 'Retaining wall avg ht (ft)', type: 'num' },
  ] },
  { title: 'Alteration intensity (% of alter occupiable)', showFor: ['Basic Services'], fields: [
    { key: 'Percent Alter Occupiable Light', label: 'Light', type: 'pct' },
    { key: 'Percent Alter Occupiable Moderate', label: 'Moderate', type: 'pct' },
    { key: 'Percent Alter Occupiable Heavy', label: 'Heavy', type: 'computed' },
    { key: 'Percent Alter Occupiable Area Specialty', label: 'Specialty (alter)', type: 'pct' },
    { key: 'Percent New Occupiable Area Specialty', label: 'Specialty (new)', type: 'pct' },
    // Phase adjustments — fine-tune SD/DA/AD/IA fee percent within their phase (Airtable percent
    // fields, e.g. SD Adjustment 0.7 = 70% of the phase's base fee). PD/DD/CD/PR have no
    // adjustment field in Airtable, so they're intentionally absent here.
    { key: 'SD Adjustment', label: 'SD Adjustment', type: 'pct' },
    { key: 'DA Adjustment', label: 'DA Adjustment', type: 'pct' },
    { key: 'AD Adjustment', label: 'AD Adjustment', type: 'pct' },
    { key: 'IA Adjustment', label: 'IA Adjustment', type: 'pct' },
  ] },
  { title: 'Phases & meetings', showFor: ['Basic Services'], fields: [
    { key: 'Phases Included', label: 'Phases included', type: 'multiselect', options: PHASES_INCLUDED_OPTIONS, full: true },
    { key: 'PD Meetings', label: 'PD meetings', type: 'num' },
    { key: 'SD Meetings', label: 'SD meetings', type: 'num' },
    { key: 'DD Meetings', label: 'DD meetings', type: 'num' },
    { key: 'CD Meetings', label: 'CD meetings', type: 'num' },
    { key: 'AD Meetings', label: 'AD meetings', type: 'num' },
    { key: 'IA Meetings', label: 'IA meetings', type: 'num' },
  ] },
  // Shown for BOTH proposal types — the Design Consultation document renders the long-distance
  // travel card and the existing-building 3D model card too, and until now neither was reachable
  // from the DC editor even though the renderer had branches for them. `3D Model` is the one
  // Basic-Services-only field here (it drives an Exhibit C presentation flag).
  { title: 'Add-ons', fields: [
    // FORMULA field: IF(AND(FIND("Alterations",{Project Involves}), NOT({Exempt As-Built Model
    // Override})), 'Yes','No'). Read-only by nature — the two levers below are what actually
    // control it, which is why the override is exposed rather than this.
    { key: 'Include As-Built Model', label: 'Include as-built model', type: 'computed' },
    { key: 'Exempt As-Built Model Override', label: 'Exempt from as-built model', type: 'bool' },
    { key: 'Client Provides As-built Drawings', label: 'Client provides as-builts', type: 'yesno' },
    { key: 'Existing Area for As built', label: 'Existing area for as-built (SF)', type: 'num' },
    { key: '3D Model', label: '3D model', type: 'yesno', showFor: ['Basic Services'] },
    // Airtable type is CHECKBOX, not a Yes/No select — declaring this 'yesno' wrote the literal
    // string 'Yes'/'No' into a checkbox field (schema-verified via describe_table).
    { key: 'Long Distance Travel', label: 'Long-distance travel', type: 'bool' },
    { key: 'Project Involves', label: 'Project involves', type: 'multiselect', options: PROJECT_INVOLVES_OPTIONS, full: true },
  ] },
  { title: 'Scope narrative', showFor: ['Basic Services'], fields: [
    { key: 'Site Features', label: 'Site features', type: 'longtext', full: true },
    { key: 'Special Architectural Features', label: 'Architectural features', type: 'longtext', full: true },
    { key: 'Interior Features', label: 'Interior features', type: 'longtext', full: true },
    { key: 'Room Inventory', label: 'Room inventory', type: 'longtext', full: true },
    // NOT free text — Airtable models this as a multipleSelects field (a checklist of interior
    // architecture scope categories), not a narrative paragraph. Writing a plain string here
    // (the bug: this used to be declared 'longtext') 422s the whole create/update call.
    { key: 'Interior Architecture Scope', label: 'Interior architecture scope', type: 'multiselect', options: INTERIOR_ARCH_SCOPE_OPTIONS, full: true },
  ] },
  { title: 'Design Consultation', hint: 'Used for Design Consultation proposals.', showFor: ['Design Consultation'], fields: [
    { key: 'Retainer Amount', label: 'Retainer', type: 'cur' },
    { key: 'Estimated BS FPV', label: 'Estimated Basic Services value', type: 'cur' },
    // `Allotment` (checkbox) is what gates the allotment paragraph in the document. There is no
    // manual toggle for it — toProposalFields derives it from Allotment Amount (>0 = offered), so
    // a dollar amount and the flag can never drift out of sync.
    { key: 'Allotment Amount', label: 'Allotment amount (offering an allotment turns on automatically once this is set)', type: 'cur' },
    { key: 'Allotment Description', label: 'Allotment description', type: 'computed', full: true },
    // Also lives in the Basic Services 'Pricing controls' group; duplicated here rather than
    // hoisted so the BS editor's field order is untouched. ALL_INPUT_FIELDS de-duplicates by key.
    { key: 'Friends and Family Discount', label: 'Industry Partner discount', type: 'bool' },
  ] },
]

/* De-duplicated by key: a field may appear in two groups with disjoint `showFor` (so it is never
 * rendered twice) purely to sit in a sensible place for each proposal type. Writers and readers
 * must still see it exactly once. */
const ALL_INPUT_FIELDS = [...new Map(PROPOSAL_INPUT_GROUPS.flatMap(g => g.fields).map(f => [f.key, f])).values()]

/**
 * Map editor values → Airtable fields.
 *
 * `only` (a Set of field keys) scopes the write to just those fields — the DIRTY-FIELD DIFF the
 * editor uses on every update. Without it a save writes the ENTIRE input set, which meant any
 * value the form happened to be holding (e.g. from a snapshot captured before someone else's —
 * or your own — earlier save) silently overwrote the newer one in Airtable. Scoping the payload
 * to what was actually touched makes that impossible, independent of how fresh the form is.
 *
 * Empty handling differs by mode, and the difference is the whole point:
 *   - UNSCOPED (create): an empty value is skipped, because there's no way to tell "the user
 *     cleared this" from "the user never touched it", and clearing a real value by accident is
 *     the worse failure.
 *   - SCOPED (update): membership in `only` IS the proof the field was touched, so an empty
 *     value is written as an explicit clear (null, or [] for links/multi-selects). This is what
 *     finally makes a field erasable from the editor at all.
 */
export function toProposalFields(vals, only) {
  const out = {}
  for (const f of ALL_INPUT_FIELDS) {
    if (f.type === 'computed') continue   // Airtable formula field — writing to it 422s regardless of typecast
    if (only && !only.has(f.key)) continue
    const v = vals[f.key]
    if (f.type === 'bool') { out[f.key] = !!v; continue }
    if (v === undefined || v === '' || v === null) {
      if (only) out[f.key] = (f.type === 'multiselect' || f.type === 'feemodel' || f.type === 'contact') ? [] : null
      continue
    }
    if (f.type === 'num' || f.type === 'cur') { const n = Number(v); if (!isNaN(n)) out[f.key] = n }
    // 'pct' fields are genuine Airtable percent fields (0.2 = 20%) — the editor shows/accepts a
    // whole percentage (20), so divide by 100 on write. These used to be declared 'num' (no
    // conversion), which let a typed "20" get stored as the literal number 20 — 100x too big,
    // breaking every formula downstream (e.g. Percent Alter Occupiable Heavy).
    else if (f.type === 'pct') { const n = Number(v); if (!isNaN(n)) out[f.key] = n / 100 }
    // An empty array only ever means 'cleared' on a scoped write; on an unscoped one it is
    // indistinguishable from 'never set', so it is skipped (same rule as the empty check above).
    else if (f.type === 'multiselect') { if (only || (Array.isArray(v) && v.length)) out[f.key] = Array.isArray(v) ? v : [] }
    else if (f.type === 'feemodel' || f.type === 'contact') { out[f.key] = Array.isArray(v) ? v : [v] }
    else out[f.key] = v   // text/longtext/date/select/yesno
  }
  // `Allotment` (checkbox) has no editor control — derived from whether an amount was entered, so
  // it can never disagree with Allotment Amount (which is what a manual toggle risked). Only
  // re-derived when the amount itself is in scope, so a scoped write never touches it blindly.
  if (!only || only.has('Allotment Amount')) out['Allotment'] = Number(vals['Allotment Amount']) > 0
  return out
}

/* Airtable writes return { error } on failure (the proxy passes the body through verbatim)
   instead of throwing — surface it so a bad write (e.g. a field-type mismatch) shows up as a
   real error in the editor instead of silently doing nothing (createProposal/updateProposal
   used to just return null on any failure, which is exactly what made the proposal-creation
   bugs here so hard to see — nothing told the user or the console why it "did nothing"). */
function throwOnError(res, what) {
  if (res && res.error) {
    const msg = (res.error && (res.error.message || res.error.type)) || ('Could not save the ' + what)
    throw new Error(msg)
  }
  return res
}

/* ── Writes (used by the authoring editor in 6b) ─────────────────────────── */
export async function createProposal(fields) {
  const res = throwOnError(await apiPost(TBL_PROP, fields, { typecast: true }), 'proposal')
  return res && res.id ? normProposal(res) : null
}
export async function updateProposal(id, fields) {
  const res = throwOnError(await apiPatch(TBL_PROP, id, fields, { typecast: true }), 'proposal')
  return res && res.id ? normProposal(res) : null
}

/**
 * Extend a proposal's expiration by adding days to its existing `Expiration Timeline` (a plain
 * days-from-`Proposal Date` count). `Expiration Date` and `Expired`/`Display Status` are all
 * Airtable formulas derived from this one field — writing it is the whole operation, nothing else
 * to touch. The alternative to Replace for a proposal the client still wants to sign but which
 * lapsed before they got to it.
 * @param {string} proposalId
 * @param {number} currentTimeline — the proposal's current `expirationTimeline` (days)
 * @param {number} extraDays — how many days to add
 */
export async function extendProposalExpiration(proposalId, currentTimeline, extraDays) {
  const newTimeline = (Number(currentTimeline) || 0) + Number(extraDays)
  return updateProposal(proposalId, { 'Expiration Timeline': newTimeline })
}

/* ── Native PDF (api/proposal-pdf.js) ────────────────────────────────────── */
async function clerkToken() {
  try { return typeof window.__clerkGetToken === 'function' ? await window.__clerkGetToken().catch(() => null) : null } catch { return null }
}
/**
 * Generate the proposal PDF. working:true = preview (WORKING DRAFT stamp). Returns a blob object URL.
 * Pass a pre-opened tab (window.open synchronously on click) to dodge popup blockers.
 */
export async function previewProposalPdf(proposalId, { working = true } = {}) {
  const token = await clerkToken()
  const res = await fetch('/api/proposal-pdf', {
    method: 'POST', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) },
    body: JSON.stringify({ proposalId, working }),
  })
  if (!res.ok) { let e; try { e = (await res.json()).error } catch (_) {} throw new Error(e || 'PDF generation failed') }
  return URL.createObjectURL(await res.blob())
}

/**
 * Preview the Client Hub web view (screen-mode HTML) before posting — the same document
 * the journey serves once the proposal is posted, without needing a posted PDF or a journey
 * link. Basic Services only. working:true = WORKING DRAFT stamp. Returns a blob object URL.
 * Pass a pre-opened tab (window.open synchronously on click) to dodge popup blockers.
 */
export async function previewProposalHtml(proposalId, { working = true } = {}) {
  const token = await clerkToken()
  const res = await fetch('/api/proposal-pdf', {
    method: 'POST', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) },
    body: JSON.stringify({ proposalId, working, format: 'html' }),
  })
  if (!res.ok) { let e; try { e = (await res.json()).error } catch (_) {} throw new Error(e || 'Web view generation failed') }
  return URL.createObjectURL(await res.blob())
}

/** Freeze the issued PDF to Storage + write it onto the proposal's Posted Proposal PDF. Returns { url }. */
export async function freezeProposalPdf(proposalId) {
  const token = await clerkToken()
  const res = await fetch('/api/proposal-pdf', {
    method: 'POST', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) },
    body: JSON.stringify({ proposalId, freeze: true }),
  })
  const d = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(d.error || 'Freeze failed')
  return d
}

/** Mark a proposal posted (after freezing). memberId → Posting Member.
 *  Posted Date is NOT sent — it's an Airtable lastModifiedTime field that watches Posted itself
 *  and auto-populates; writing to it directly 422s ("field is computed").
 *  Status: 'Posted' is set explicitly here too — this used to be set only by an Airtable
 *  automation watching the Posted Proposals view (retired; see docs/CRM.md and
 *  services/opportunities.js#advanceToProposalPosted for the other native replacements). */
export function markPosted(proposalId, { memberId } = {}) {
  const fields = { 'Posted': true, 'Status': 'Posted' }
  if (memberId) fields['Posting Member'] = [memberId]
  return updateProposal(proposalId, fields)
}

/**
 * TEST/ADMIN ONLY (/debug mode). Fully reverts Post & sign state — clears Posted/Accepted on
 * Airtable and removes the journey card + signature rows on Supabase — so the full client-journey
 * post -> sign -> executed cycle can be re-tested from scratch. See api/proposal-reset.js.
 */
export async function resetProposal(proposalId) {
  const token = await clerkToken()
  const res = await fetch('/api/proposal-reset', {
    method: 'POST', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) },
    body: JSON.stringify({ proposalId }),
  })
  const d = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(d.error || 'Reset failed')
  return d
}

/**
 * Countersignature status — whether the client has signed and is waiting on Alder James, and (once
 * done) who countersigned and when. See api/proposal-countersign.js.
 */
export async function getCountersignStatus(proposalId) {
  const token = await clerkToken()
  const res = await fetch('/api/proposal-countersign?proposalId=' + encodeURIComponent(proposalId), {
    headers: token ? { Authorization: 'Bearer ' + token } : {},
  })
  const d = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(d.error || 'Could not load countersign status')
  return d
}

/**
 * Countersign a client-signed proposal with the caller's own saved signature (Settings > Personal
 * Signature). Principal-only (admin module). Produces the true final/executed PDF.
 */
export async function countersignProposal(proposalId) {
  const token = await clerkToken()
  const res = await fetch('/api/proposal-countersign', {
    method: 'POST', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) },
    body: JSON.stringify({ proposalId }),
  })
  const d = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(d.error || 'Countersign failed')
  return d
}

/**
 * Record a proposal as executed from a signed hard copy the client returned OUTSIDE the app
 * (printed, signed by hand, scanned, emailed back) instead of e-signing in the Client Hub.
 * Principal-only (admin module) — same authority as countersignProposal, since the consequences
 * are identical (fires the same onboarding kickoff, locks the contract). See
 * api/proposal-manual-execute.js.
 * @param {string} proposalId
 * @param {File} file — the signed PDF scan
 * @param {{ selectedPackage?: string }} [opts]
 */
export async function recordProposalExecutedManually(proposalId, file, { selectedPackage } = {}) {
  const token = await clerkToken()
  const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) }

  const credRes = await fetch('/api/proposal-manual-execute', {
    method: 'POST', headers, body: JSON.stringify({ action: 'getUploadUrl', proposalId, filename: file.name }),
  })
  const cred = await credRes.json().catch(() => ({}))
  if (!credRes.ok) throw new Error(cred.error || 'Could not get upload URL')

  const uploadRes = await fetch(cred.signedUrl, {
    method: 'PUT', headers: { 'Content-Type': file.type || 'application/pdf', 'x-upsert': 'true' }, body: file,
  })
  if (!uploadRes.ok) {
    const detail = await uploadRes.text().catch(() => '')
    throw new Error('Storage upload failed (' + uploadRes.status + ')' + (detail ? ': ' + detail.slice(0, 200) : ''))
  }

  const execRes = await fetch('/api/proposal-manual-execute', {
    method: 'POST', headers,
    body: JSON.stringify({ action: 'execute', proposalId, path: cred.path, filename: file.name, selectedPackage }),
  })
  const d = await execRes.json().catch(() => ({}))
  if (!execRes.ok) throw new Error(d.error || 'Could not record the execution')
  return d
}
